import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Collapse,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  PersonSearch as PersonSearchIcon,
  HelpOutline as HelpOutlineIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../../../utils/config';
import { EmployeeOutreachDialog } from './EmployeeOutreachDialog';
import { MatchPaymentDialog } from './MatchPaymentDialog';

interface PaymentMatch {
  id: number;
  paymentQuickbooksId: string;
  paymentAmount: number;
  paymentDate: string;
  paymentCustomerName?: string;
  paymentMethod?: string;
  paymentReference?: string;
  matchStatus: string;
  matchedInvoiceIds?: string[];
  matchedInvoiceTotal?: number;
  matchedAt?: string;
  matchedBy?: string;
  outreachEmployeeName?: string;
  outreachSentAt?: string;
  outreachQuestion?: string;
  outreachResponse?: string;
  candidateInvoices?: CandidateInvoice[];
}

interface CandidateInvoice {
  invoiceId: string;
  invoiceNumber: string;
  customerName: string;
  amount: number;
  balance: number;
  dueDate?: string;
  score: number;
  reasons: string[];
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'matched':
      return 'success';
    case 'awaiting_response':
      return 'warning';
    case 'no_match':
      return 'error';
    case 'candidate_suggested':
      return 'info';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'unmatched':
      return 'Unmatched';
    case 'candidate_suggested':
      return 'Candidates Found';
    case 'awaiting_response':
      return 'Awaiting Response';
    case 'matched':
      return 'Matched';
    case 'no_match':
      return 'No Match';
    default:
      return status;
  }
};

interface PaymentCardProps {
  payment: PaymentMatch;
  onRefreshCandidates: (paymentId: string) => Promise<void>;
  onMatch: (paymentId: string, invoiceIds: string[], notes?: string) => Promise<void>;
  onOutreach: (paymentId: string, employeeId: string, employeeName: string, employeeEmail: string, question: string, sendEmail: boolean) => Promise<void>;
  onNoMatch: (paymentId: string, reason: string) => Promise<void>;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
  payment,
  onRefreshCandidates,
  onMatch,
  onOutreach,
  onNoMatch,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showOutreachDialog, setShowOutreachDialog] = useState(false);
  const [showMatchDialog, setShowMatchDialog] = useState(false);

  const candidates = payment.candidateInvoices || [];
  const selectedTotal = candidates
    .filter(c => selectedInvoices.includes(c.invoiceId))
    .reduce((sum, c) => sum + c.balance, 0);

  const handleToggleInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleRefreshCandidates = async () => {
    setLoading(true);
    try {
      await onRefreshCandidates(payment.paymentQuickbooksId);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatch = async (notes?: string) => {
    setLoading(true);
    try {
      await onMatch(payment.paymentQuickbooksId, selectedInvoices, notes);
      setShowMatchDialog(false);
      setSelectedInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOutreach = async (employeeId: string, employeeName: string, employeeEmail: string, question: string, sendEmail: boolean) => {
    setLoading(true);
    try {
      await onOutreach(payment.paymentQuickbooksId, employeeId, employeeName, employeeEmail, question, sendEmail);
      setShowOutreachDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const amountMatch = Math.abs(payment.paymentAmount - selectedTotal) < 0.01;
  const withinTolerance = Math.abs(payment.paymentAmount - selectedTotal) <= payment.paymentAmount * 0.05;

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {formatCurrency(payment.paymentAmount)}
                <Chip
                  label={getStatusLabel(payment.matchStatus)}
                  color={getStatusColor(payment.matchStatus)}
                  size="small"
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {payment.paymentCustomerName || 'Unknown Customer'} - {formatDate(payment.paymentDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {payment.paymentMethod || 'Unknown Method'}
                {payment.paymentReference && ` | Ref: ${payment.paymentReference}`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh candidate invoices">
                <IconButton size="small" onClick={handleRefreshCandidates} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Outreach Info */}
          {payment.matchStatus === 'awaiting_response' && payment.outreachEmployeeName && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<AccessTimeIcon />}>
              <Typography variant="body2">
                Asked <strong>{payment.outreachEmployeeName}</strong> on {formatDate(payment.outreachSentAt!)}:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                "{payment.outreachQuestion}"
              </Typography>
              {payment.outreachResponse && (
                <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                  Response: {payment.outreachResponse}
                </Typography>
              )}
            </Alert>
          )}

          {/* Matched Info */}
          {payment.matchStatus === 'matched' && payment.matchedInvoiceIds && (
            <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
              <Typography variant="body2">
                Matched to {payment.matchedInvoiceIds.length} invoice(s) totaling {formatCurrency(payment.matchedInvoiceTotal || 0)}
              </Typography>
            </Alert>
          )}
        </CardContent>

        {/* Candidate Invoices Section */}
        {payment.matchStatus !== 'matched' && payment.matchStatus !== 'no_match' && (
          <>
            <Divider />
            <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {candidates.length > 0
                  ? `${candidates.length} Candidate Invoice${candidates.length > 1 ? 's' : ''}`
                  : 'No Candidates Found'
                }
              </Button>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonSearchIcon />}
                  onClick={() => setShowOutreachDialog(true)}
                  disabled={loading}
                >
                  Ask Employee
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => onNoMatch(payment.paymentQuickbooksId, 'No matching invoice found')}
                  disabled={loading}
                >
                  Mark No Match
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setShowMatchDialog(true)}
                  disabled={selectedInvoices.length === 0 || loading}
                >
                  Match Selected
                </Button>
              </Stack>
            </CardActions>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <CardContent sx={{ pt: 0 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : candidates.length === 0 ? (
                  <Alert severity="info">
                    No candidate invoices found. Click refresh to search again or ask an employee for help.
                  </Alert>
                ) : (
                  <>
                    {candidates.map((candidate) => (
                      <Box
                        key={candidate.invoiceId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: selectedInvoices.includes(candidate.invoiceId) ? 'action.selected' : 'background.paper',
                          border: '1px solid',
                          borderColor: selectedInvoices.includes(candidate.invoiceId) ? 'primary.main' : 'divider',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedInvoices.includes(candidate.invoiceId)}
                              onChange={() => handleToggleInvoice(candidate.invoiceId)}
                            />
                          }
                          label=""
                          sx={{ mr: 0 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            INV-{candidate.invoiceNumber} - {candidate.customerName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(candidate.balance)} | Due: {candidate.dueDate ? formatDate(candidate.dueDate) : 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 150, textAlign: 'right' }}>
                          <Tooltip title={candidate.reasons.join(', ')}>
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Score: {candidate.score}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={candidate.score}
                                color={candidate.score >= 70 ? 'success' : candidate.score >= 40 ? 'warning' : 'error'}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}

                    {/* Selection Summary */}
                    {selectedInvoices.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2">
                          Selected: {selectedInvoices.length} invoice(s) totaling {formatCurrency(selectedTotal)}
                        </Typography>
                        {amountMatch ? (
                          <Typography variant="body2" color="success.main">
                            Matches payment amount
                          </Typography>
                        ) : withinTolerance ? (
                          <Typography variant="body2" color="warning.main">
                            Within 5% tolerance (difference: {formatCurrency(Math.abs(payment.paymentAmount - selectedTotal))})
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="error.main">
                            Does not match payment amount (difference: {formatCurrency(Math.abs(payment.paymentAmount - selectedTotal))})
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Collapse>
          </>
        )}
      </Card>

      <EmployeeOutreachDialog
        open={showOutreachDialog}
        payment={payment}
        onClose={() => setShowOutreachDialog(false)}
        onConfirm={handleConfirmOutreach}
      />

      <MatchPaymentDialog
        open={showMatchDialog}
        payment={payment}
        selectedInvoices={candidates.filter(c => selectedInvoices.includes(c.invoiceId))}
        onClose={() => setShowMatchDialog(false)}
        onConfirm={handleConfirmMatch}
      />
    </>
  );
};

interface PaymentMatchingSectionProps {
  onRefresh?: () => void;
}

export const PaymentMatchingSection: React.FC<PaymentMatchingSectionProps> = ({ onRefresh }) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [payments, setPayments] = useState<PaymentMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/payments/unmatched?matchStatus=unmatched,candidate_suggested,awaiting_response`
      );
      if (!response) throw new Error('No response received');
      const data = await response.json();

      if (data.success) {
        setPayments(data.data.items);
      } else {
        setError(data.error || 'Failed to load payments');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleRefreshCandidates = async (paymentId: string) => {
    const response = await authenticatedFetch(
      `${API_URL}/api/accounting/payments/${paymentId}/candidates?regenerate=true`
    );
    if (!response) throw new Error('No response received');
    const data = await response.json();

    if (data.success) {
      // Update the payment in state with new candidates
      setPayments(prev => prev.map(p =>
        p.paymentQuickbooksId === paymentId
          ? { ...p, candidateInvoices: data.data.candidates, matchStatus: data.data.candidates.length > 0 ? 'candidate_suggested' : 'unmatched' }
          : p
      ));
    }
  };

  const handleMatch = async (paymentId: string, invoiceIds: string[], notes?: string) => {
    const response = await authenticatedFetch(
      `${API_URL}/api/accounting/payments/${paymentId}/match`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceIds, notes }),
      }
    );
    if (!response) throw new Error('No response received');
    const data = await response.json();

    if (data.success) {
      // Remove matched payment from list or update status
      setPayments(prev => prev.filter(p => p.paymentQuickbooksId !== paymentId));
      onRefresh?.();
    } else {
      throw new Error(data.error || 'Failed to match payment');
    }
  };

  const handleOutreach = async (
    paymentId: string,
    employeeId: string,
    employeeName: string,
    employeeEmail: string,
    question: string,
    sendEmail: boolean
  ) => {
    const response = await authenticatedFetch(
      `${API_URL}/api/accounting/payments/${paymentId}/outreach`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId, employeeName, employeeEmail, question, sendEmail }),
      }
    );
    if (!response) throw new Error('No response received');
    const data = await response.json();

    if (data.success) {
      // Update payment in state
      setPayments(prev => prev.map(p =>
        p.paymentQuickbooksId === paymentId
          ? {
              ...p,
              matchStatus: 'awaiting_response',
              outreachEmployeeName: employeeName,
              outreachSentAt: new Date().toISOString(),
              outreachQuestion: question,
            }
          : p
      ));
    } else {
      throw new Error(data.error || 'Failed to record outreach');
    }
  };

  const handleNoMatch = async (paymentId: string, reason: string) => {
    const response = await authenticatedFetch(
      `${API_URL}/api/accounting/payments/${paymentId}/no-match`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }
    );
    if (!response) throw new Error('No response received');
    const data = await response.json();

    if (data.success) {
      // Remove from list
      setPayments(prev => prev.filter(p => p.paymentQuickbooksId !== paymentId));
    } else {
      throw new Error(data.error || 'Failed to mark as no match');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            Payments Needing Invoice Matching
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Match incoming payments to their corresponding invoices
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchPayments}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : payments.length === 0 ? (
        <Alert severity="success">
          All payments have been matched to invoices.
        </Alert>
      ) : (
        payments.map((payment) => (
          <PaymentCard
            key={payment.id}
            payment={payment}
            onRefreshCandidates={handleRefreshCandidates}
            onMatch={handleMatch}
            onOutreach={handleOutreach}
            onNoMatch={handleNoMatch}
          />
        ))
      )}
    </Box>
  );
};

export default PaymentMatchingSection;
